<template>
  <div class="main-container">
    <div class="sampling-configuration-page">
      <div class="sampling-configuration-page__left">
        <main-menu></main-menu>
      </div>
      <div class="sampling-configuration-page__right">
        <div class="form--overflow">
          <div class="form__row">
            <div class="form__header">
              <h2>Sampling Configuration</h2>
              <p>Manage AppInsights configuration for games and crosswords</p>
            </div>
          </div>

          <!-- Tab Navigation -->
          <div class="form__row">
            <div class="form__tabs">
              <v-tabs
                v-model="activeTab"
                background-color="transparent"
                color="primary"
                grow
              >
                <v-tab>
                  <v-icon left>mdi-gamepad-variant</v-icon>
                  Games Sampling
                </v-tab>
                <v-tab>
                  <v-icon left>mdi-puzzle</v-icon>
                  Crosswords Sampling
                </v-tab>
              </v-tabs>
            </div>
          </div>

          <!-- Tab Content -->
          <v-window v-model="activeTab">
                        <!-- Games Sampling Tab -->
            <v-window-item :value="0">
              <div class="form__row">
                <div class="form__actions">
                  <v-btn 
                    color="success" 
                    @click="saveGamesConfig"
                    :loading="gamesSaving"
                    :disabled="!isGamesValidJson || gamesSaving"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    Save Games Configuration
                  </v-btn>
                  <v-btn 
                    color="info" 
                    @click="formatGamesJson"
                    :disabled="!gamesJsonText"
                    class="ml-3"
                  >
                    <v-icon left>mdi-format-align-left</v-icon>
                    Format JSON
                  </v-btn>
                  <v-btn 
                    color="warning" 
                    @click="exportGamesConfig"
                    :disabled="!isGamesValidJson"
                    class="ml-3"
                  >
                    <v-icon left>mdi-download</v-icon>
                    Export
                  </v-btn>
                  <v-btn 
                    color="secondary" 
                    @click="importGamesConfig"
                    class="ml-3"
                  >
                    <v-icon left>mdi-upload</v-icon>
                    Import
                  </v-btn>
                </div>
              </div>

            <div class="form__row" v-if="gamesError">
              <v-alert type="error" dismissible @click:close="gamesError = ''">
                {{ gamesError }}
              </v-alert>
            </div>

            <div class="form__row" v-if="gamesSuccess">
              <v-alert type="success" dismissible @click:close="gamesSuccess = ''">
                {{ gamesSuccess }}
              </v-alert>
            </div>

            <div class="form__row">
              <div class="form__field">
                <label class="form__label">Games Configuration JSON:</label>
                <v-textarea
                  v-model="gamesJsonText"
                  :error-messages="gamesJsonError"
                  :success="isGamesValidJson && gamesJsonText"
                  outlined
                  rows="20"
                  placeholder="Paste or edit JSON configuration for games here..."
                  @input="validateGamesJson"
                  class="json-textarea"
                ></v-textarea>
              </div>
            </div>

            <div class="form__row" v-if="gamesConfigData.length > 0">
              <div class="form__field">
                <label class="form__label">Games Configuration Summary:</label>
                <div class="config-summary">
                  <div class="summary-item">
                    <strong>Total Games:</strong> {{ gamesConfigData.length }}
                  </div>
                  <div class="summary-item">
                    <strong>Games with Sampling:</strong> {{ gamesWithSampling }}
                  </div>
                  <div class="summary-item">
                    <strong>Games with Domain Selection:</strong> {{ gamesWithDomains }}
                  </div>
                  <div class="summary-item">
                    <strong>Total Unique Domains:</strong> {{ totalDomains }}
                  </div>
                  <div class="summary-item">
                    <strong>Average Sampling Percentage:</strong> {{ averageSamplingPercentage }}%
                  </div>
                </div>
              </div>
            </div>
          </v-window-item>

                        <!-- Crosswords Sampling Tab -->
            <v-window-item :value="1">
              <div class="form__row">
                <div class="form__actions">
                  <v-btn 
                    color="success" 
                    @click="saveCrosswordsConfig"
                    :loading="crosswordsSaving"
                    :disabled="!isCrosswordsValidJson || crosswordsSaving"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    Save Crosswords Configuration
                  </v-btn>
                  <v-btn 
                    color="info" 
                    @click="formatCrosswordsJson"
                    :disabled="!crosswordsJsonText"
                    class="ml-3"
                  >
                    <v-icon left>mdi-format-align-left</v-icon>
                    Format JSON
                  </v-btn>
                  <v-btn 
                    color="warning" 
                    @click="exportCrosswordsConfig"
                    :disabled="!isCrosswordsValidJson"
                    class="ml-3"
                  >
                    <v-icon left>mdi-download</v-icon>
                    Export
                  </v-btn>
                  <v-btn 
                    color="secondary" 
                    @click="importCrosswordsConfig"
                    class="ml-3"
                  >
                    <v-icon left>mdi-upload</v-icon>
                    Import
                  </v-btn>
                </div>
              </div>

            <div class="form__row" v-if="crosswordsError">
              <v-alert type="error" dismissible @click:close="crosswordsError = ''">
                {{ crosswordsError }}
              </v-alert>
            </div>

            <div class="form__row" v-if="crosswordsSuccess">
              <v-alert type="success" dismissible @click:close="crosswordsSuccess = ''">
                {{ crosswordsSuccess }}
              </v-alert>
            </div>

            <div class="form__row">
              <div class="form__field">
                <label class="form__label">Crosswords Configuration JSON:</label>
                <v-textarea
                  v-model="crosswordsJsonText"
                  :error-messages="crosswordsJsonError"
                  :success="isCrosswordsValidJson && crosswordsJsonText"
                  outlined
                  rows="20"
                  placeholder="Paste or edit JSON configuration for crosswords here..."
                  @input="validateCrosswordsJson"
                  class="json-textarea"
                ></v-textarea>
              </div>
            </div>

            <div class="form__row" v-if="crosswordsConfigData.length > 0">
              <div class="form__field">
                <label class="form__label">Crosswords Configuration Summary:</label>
                <div class="config-summary">
                  <div class="summary-item">
                    <strong>Total Crosswords:</strong> {{ crosswordsConfigData.length }}
                  </div>
                  <div class="summary-item">
                    <strong>Crosswords with Sampling:</strong> {{ crosswordsWithSampling }}
                  </div>
                  <div class="summary-item">
                    <strong>Crosswords with Domain Selection:</strong> {{ crosswordsWithDomains }}
                  </div>
                  <div class="summary-item">
                    <strong>Total Unique Domains:</strong> {{ crosswordsTotalDomains }}
                  </div>
                  <div class="summary-item">
                    <strong>Average Sampling Percentage:</strong> {{ crosswordsAverageSamplingPercentage }}%
                  </div>
                </div>
              </div>
            </div>
          </v-window-item>
          </v-window>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainMenu from '../MainMenu/MainMenu.vue'
import AnalyticsScoresService from '../../services/AnalyticsScoresService.js'
import CrosswordsService from '../../services/CrosswordsService.js'

export default {
  name: 'SamplingConfiguration',
  components: { MainMenu },
  data() {
    return {
      // Tab management
      activeTab: 0,
      
      // Games configuration
      gamesJsonText: '',
      gamesConfigData: [],
      gamesSaving: false,
      gamesError: '',
      gamesSuccess: '',
      gamesJsonError: '',
      isGamesValidJson: false,
      
      // Crosswords configuration
      crosswordsJsonText: '',
      crosswordsConfigData: [],
      crosswordsSaving: false,
      crosswordsError: '',
      crosswordsSuccess: '',
      crosswordsJsonError: '',
      isCrosswordsValidJson: false,
      
      // Services
      analyticsService: null,
      crosswordsService: null // Will be initialized when API endpoint is provided
    }
  },
  created() {
    // Component created
  },
  computed: {
    // Games computed properties
    gamesWithSampling() {
      return this.gamesConfigData.filter(game => game.defaultSamplingPercentage > 0).length
    },
    gamesWithDomains() {
      return this.gamesConfigData.filter(game => game.selectedDomains && game.selectedDomains.length > 0).length
    },
    totalDomains() {
      const allDomains = new Set()
      this.gamesConfigData.forEach(game => {
        if (game.selectedDomains) {
          game.selectedDomains.forEach(domain => allDomains.add(domain))
        }
      })
      return allDomains.size
    },
    averageSamplingPercentage() {
      const gamesWithSampling = this.gamesConfigData.filter(game => game.defaultSamplingPercentage > 0)
      if (gamesWithSampling.length === 0) return 0
      
      const total = gamesWithSampling.reduce((sum, game) => sum + game.defaultSamplingPercentage, 0)
      return Math.round(total / gamesWithSampling.length)
    },
    
    // Crosswords computed properties
    crosswordsWithSampling() {
      return this.crosswordsConfigData.filter(crossword => crossword.defaultSamplingPercentage > 0).length
    },
    crosswordsWithDomains() {
      return this.crosswordsConfigData.filter(crossword => crossword.selectedDomains && crossword.selectedDomains.length > 0).length
    },
    crosswordsTotalDomains() {
      const allDomains = new Set()
      this.crosswordsConfigData.forEach(crossword => {
        if (crossword.selectedDomains) {
          crossword.selectedDomains.forEach(domain => allDomains.add(domain))
        }
      })
      return allDomains.size
    },
    crosswordsAverageSamplingPercentage() {
      const crosswordsWithSampling = this.crosswordsConfigData.filter(crossword => crossword.defaultSamplingPercentage > 0)
      if (crosswordsWithSampling.length === 0) return 0
      
      const total = crosswordsWithSampling.reduce((sum, crossword) => sum + crossword.defaultSamplingPercentage, 0)
      return Math.round(total / crosswordsWithSampling.length)
    }
  },
  mounted() {
    try {
      // Инициализируем сервис с MSAL
      this.analyticsService = new AnalyticsScoresService(this.msal)
      
      // Initialize crosswords service
      this.crosswordsService = new CrosswordsService(this.msal)
      
      // Автоматически загружаем конфигурацию игр при загрузке страницы
      this.loadGamesConfig()
    } catch (error) {
      console.error('Error initializing service:', error)
      this.analyticsService = null
    }
  },
  watch: {
    activeTab(newTab) {
      // Load crosswords config when switching to crosswords tab
      if (newTab === 1 && this.crosswordsConfigData.length === 0) {
        this.loadCrosswordsConfig()
      }
    }
  },
  methods: {
    // Games methods
    async loadGamesConfig() {
      if (!this.analyticsService) {
        this.gamesError = 'Service not initialized'
        return
      }
      
      this.gamesLoading = true
      this.gamesError = ''
      this.gamesSuccess = ''
      
      try {
        const data = await this.analyticsService.loadConfig()
        this.gamesConfigData = data
        this.gamesJsonText = JSON.stringify(data, null, 2)
        this.validateGamesJson()
      } catch (err) {
        console.error('Error in loadGamesConfig:', err)
        this.gamesError = `Failed to load games configuration: ${err.message}`
      } finally {
        this.gamesLoading = false
      }
    },

    validateGamesJson() {
      if (!this.gamesJsonText.trim()) {
        this.gamesJsonError = ''
        this.isGamesValidJson = false
        this.gamesConfigData = []
        return
      }

      try {
        const parsed = JSON.parse(this.gamesJsonText)
        
        // Use service validation
        const validationErrors = this.analyticsService.validateConfig(parsed)
        
        if (validationErrors.length > 0) {
          throw new Error(validationErrors.join(', '))
        }

        this.gamesConfigData = parsed
        this.gamesJsonError = ''
        this.isGamesValidJson = true
      } catch (err) {
        this.gamesJsonError = `Invalid JSON: ${err.message}`
        this.isGamesValidJson = false
        this.gamesConfigData = []
      }
    },

    formatGamesJson() {
      if (!this.gamesJsonText.trim()) return
      
      try {
        const parsed = JSON.parse(this.gamesJsonText)
        this.gamesJsonText = JSON.stringify(parsed, null, 2)
        this.validateGamesJson()
      } catch (err) {
        this.gamesError = `Cannot format invalid JSON: ${err.message}`
      }
    },

    async saveGamesConfig() {
      if (!this.isGamesValidJson) {
        this.gamesError = 'Cannot save invalid JSON configuration'
        return
      }

      this.gamesSaving = true
      this.gamesError = ''
      this.gamesSuccess = ''

      try {
        await this.analyticsService.saveConfig(this.gamesConfigData)
        this.gamesSuccess = 'Games configuration saved successfully!'
      } catch (err) {
        this.gamesError = `Failed to save games configuration: ${err.message}`
        console.error('Error saving games config:', err)
      } finally {
        this.gamesSaving = false
      }
    },

    exportGamesConfig() {
      if (!this.isGamesValidJson) return
      
      const dataStr = JSON.stringify(this.gamesConfigData, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      
      const link = document.createElement('a')
      link.href = URL.createObjectURL(dataBlob)
      link.download = 'games-sampling-config.json'
      link.click()
      URL.revokeObjectURL(link.href)
      
      this.gamesSuccess = 'Games configuration exported successfully!'
    },

    importGamesConfig() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.json'
      
      input.onchange = (event) => {
        const file = event.target.files[0]
        if (!file) return
        
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const content = e.target.result
            this.gamesJsonText = content
            this.validateGamesJson()
            this.gamesSuccess = 'Games configuration imported successfully!'
          } catch (err) {
            this.gamesError = `Failed to import file: ${err.message}`
          }
        }
        reader.readAsText(file)
      }
      
      input.click()
    },

    // Crosswords methods
    async loadCrosswordsConfig() {
      if (!this.crosswordsService) {
        this.crosswordsError = 'Crosswords service not initialized. Please provide API endpoint.'
        return
      }
      
      this.crosswordsLoading = true
      this.crosswordsError = ''
      this.crosswordsSuccess = ''
      
      try {
        const data = await this.crosswordsService.loadConfig()
        this.crosswordsConfigData = data
        this.crosswordsJsonText = JSON.stringify(data, null, 2)
        this.validateCrosswordsJson()
      } catch (err) {
        console.error('Error in loadCrosswordsConfig:', err)
        this.crosswordsError = `Failed to load crosswords configuration: ${err.message}`
      } finally {
        this.crosswordsLoading = false
      }
    },

    validateCrosswordsJson() {
      if (!this.crosswordsJsonText.trim()) {
        this.crosswordsJsonError = ''
        this.isCrosswordsValidJson = false
        this.crosswordsConfigData = []
        return
      }

      try {
        const parsed = JSON.parse(this.crosswordsJsonText)
        
        // Use crosswords service validation
        const validationErrors = this.crosswordsService.validateConfig(parsed)
        
        if (validationErrors.length > 0) {
          throw new Error(validationErrors.join(', '))
        }

        this.crosswordsConfigData = parsed
        this.crosswordsJsonError = ''
        this.isCrosswordsValidJson = true
      } catch (err) {
        this.crosswordsJsonError = `Invalid JSON: ${err.message}`
        this.isCrosswordsValidJson = false
        this.crosswordsConfigData = []
      }
    },

    formatCrosswordsJson() {
      if (!this.crosswordsJsonText.trim()) return
      
      try {
        const parsed = JSON.parse(this.crosswordsJsonText)
        this.crosswordsJsonText = JSON.stringify(parsed, null, 2)
        this.validateCrosswordsJson()
      } catch (err) {
        this.crosswordsError = `Cannot format invalid JSON: ${err.message}`
      }
    },

    async saveCrosswordsConfig() {
      if (!this.isCrosswordsValidJson) {
        this.crosswordsError = 'Cannot save invalid JSON configuration'
        return
      }

      if (!this.crosswordsService) {
        this.crosswordsError = 'Crosswords service not initialized. Please provide API endpoint.'
        return
      }

      this.crosswordsSaving = true
      this.crosswordsError = ''
      this.crosswordsSuccess = ''

      try {
        await this.crosswordsService.saveConfig(this.crosswordsConfigData)
        this.crosswordsSuccess = 'Crosswords configuration saved successfully!'
      } catch (err) {
        this.crosswordsError = `Failed to save crosswords configuration: ${err.message}`
        console.error('Error saving crosswords config:', err)
      } finally {
        this.crosswordsSaving = false
      }
    },

    exportCrosswordsConfig() {
      if (!this.isCrosswordsValidJson) return
      
      const dataStr = JSON.stringify(this.crosswordsConfigData, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      
      const link = document.createElement('a')
      link.href = URL.createObjectURL(dataBlob)
      link.download = 'crosswords-sampling-config.json'
      link.click()
      URL.revokeObjectURL(link.href)
      
      this.crosswordsSuccess = 'Crosswords configuration exported successfully!'
    },

    importCrosswordsConfig() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.json'
      
      input.onchange = (event) => {
        const file = event.target.files[0]
        if (!file) return
        
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const content = e.target.result
            this.crosswordsJsonText = content
            this.validateCrosswordsJson()
            this.crosswordsSuccess = 'Crosswords configuration imported successfully!'
          } catch (err) {
            this.crosswordsError = `Failed to import file: ${err.message}`
          }
        }
        reader.readAsText(file)
      }
      
      input.click()
    }
  }
}
</script>

<style scoped lang="scss" src="./SamplingConfiguration.scss"></style> 